// Builds the baked character sprites from whatever heads are available, for the
// paths that don't go through the Capture phase (the "template" / Nhân vật mẫu
// flow, and returning players). Mirrors Capture's bake logic in one reusable
// place: a real composed head for each emotion the player has on the BE, else
// the gender's default-icon head normalized to 512².
//
// (Capture still has its own inline copy for now; step 3 of the rebuild can
// converge both onto this helper.)

import { bakeCharacter } from "./bake";
import { apiBase } from "../api/client";
import type {
  BakeHeads,
  BakedCharacter,
  Context,
  Emotion,
  SharedAssetMap,
} from "../state/types";

const EMOTIONS: Emotion[] = ["neutral", "happy", "sad"];
const HEAD_NORM_PX = 512; // matches game/bake.ts HEAD_SRC

function loadImg(src: string, cors: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (cors) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`image: ${src}`));
    img.src = src;
  });
}

// Default-icon head (body_{m,f}{theme}_e{0,1,2}) normalized onto a 512² canvas so
// it bakes identically to a composed head. Per-theme (client, 2026-06): the
// default head differs by theme, just like the body.
async function defaultHead(
  isMale: boolean,
  theme: Context["theme"],
  emotion: Emotion,
): Promise<HTMLCanvasElement> {
  const g = isMale ? "m" : "f";
  const idx = emotion === "neutral" ? 0 : emotion === "happy" ? 1 : 2;
  const img = await loadImg(`/textures/body_${g}${theme}_e${idx}.png`, false);
  const canvas = document.createElement("canvas");
  canvas.width = HEAD_NORM_PX;
  canvas.height = HEAD_NORM_PX;
  canvas.getContext("2d")!.drawImage(img, 0, 0, HEAD_NORM_PX, HEAD_NORM_PX);
  return canvas;
}

/** Bake the character for `ctx` using `shared` (already preloaded). Uses the
 *  player's stored composed head per emotion when present (returning player),
 *  otherwise the gender default. Pass `forceDefaultHeads` for the "Nhân vật mẫu"
 *  (template) path, which always plays the DEFAULT character regardless of any
 *  stored face — the player's own face is used via the "custom" path instead. */
export async function buildCharacterSprites(
  ctx: Context,
  shared: SharedAssetMap,
  forceDefaultHeads = false,
): Promise<BakedCharacter> {
  const isMale = ctx.isMale ?? true;
  const bodyFrames = isMale ? shared.bodyFramesMale : shared.bodyFramesFemale;
  const ch = forceDefaultHeads ? undefined : ctx.assets.character;
  const bust = Date.now();
  // Emotions with no composed face fall back to the default chibi head; the bake
  // frames those with visual.headDefault instead of visual.head.
  const defaultHeads = new Set<Emotion>(EMOTIONS.filter((e) => !ch?.[e]));

  const heads = {} as Record<Emotion, BakeHeads[Emotion]>;
  await Promise.all(
    EMOTIONS.map(async (e) => {
      const url = ch?.[e];
      heads[e] = url
        ? await loadImg(url.startsWith("http") ? `${url}?r=${bust}` : `${apiBase()}${url}?r=${bust}`, true)
        : await defaultHead(isMale, ctx.theme, e);
    }),
  );

  const hand = isMale ? shared.handMale : shared.handFemale;
  return bakeCharacter(heads, bodyFrames, shared.config.characterVisual, hand, defaultHeads);
}
